import * as errorMonitoring from "../../utils/error-monitoring";
import * as tagManager from "../../utils/tag-manager";

import { AppUser, BootstrapData } from "../../lib/auth";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { logoutPosthogUser, setPosthogUser } from "../../utils/analytics";

import { AuthContext } from "./AuthContext";
import authApi from "../../api/authApi";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";

type AuthProviderProps = {
  whenLoading: ReactNode;
  whenAuthenticated: ReactNode;
  whenUnAuthenticated: ReactNode;
};

/**
 * Provides the current user and login/logout functionality to the app.
 *
 * There are two important authentication flows:
 *
 * 1. A fresh load of the application:
 *    i. Check if the user is authenticated via Auth0.
 *       If they are, bootstrap the app with the Auth0 flow.
 *   ii. If not, attempt to "refresh" the user's token via Core API.
 *       If it cannot refresh then the user is not logged in and routed to the sign in page.
 * 2. Explicit login:
 *    i. User logs in via Auth0.
 *       On success, they are redirected back to the app
 *       and we start the first auth flow from the top.
 *   ii. User logs in by supplying username and password directly to this application.
 *       On success, we store the access_token and send it on every API request.
 *
 */
export const AuthProvider = ({
  whenLoading,
  whenAuthenticated,
  whenUnAuthenticated,
}: AuthProviderProps) => {
  const auth0 = useAuth0();

  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const queryClient = useQueryClient();

  const handleAuthSuccess = (user: AppUser) => {
    setCurrentUser(user);
    setPosthogUser(user);
    tagManager.pushUserToDataLayer(user);
    errorMonitoring.setBugsnagUser(user);
  };

  const handleLogoutSuccess = () => {
    setCurrentUser(null);
    logoutPosthogUser();
    queryClient.clear();
    errorMonitoring.clearBugsnagUser();
  };

  const bootstrap = useCallback((bootstrapData: BootstrapData) => {
    authApi
      .bootstrapApp(bootstrapData)
      .then(handleAuthSuccess)
      .catch(() => {
        console.warn("failed to bootstrap");
      })
      .finally(() => setIsInitialized(true));
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const authData = await authApi.getAuthToken(username, password);
      return bootstrap({ kind: "cs-core", authData });
    } catch {
      throw new Error("Failed to login");
    }
  };

  const logout = async () => {
    try {
      await auth0.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
      await authApi.logout();
      handleLogoutSuccess();
    } catch {
      console.warn("failed to log out");
    }
  };

  /**
   * Wait for Auth0 to load and the check if the user is authenticated via Auth0.
   * If they are, we can bootstrap the app with the Auth0 flow.
   * If they are not, bootstrap the app using Core API's auth flow.
   */
  useEffect(() => {
    if (auth0.isLoading) return;

    auth0.isAuthenticated
      ? bootstrap({
          kind: "auth0",
          fetchAccessToken: auth0.getAccessTokenSilently,
        })
      : bootstrap({ kind: "token-refresh" });
  }, [
    auth0.isLoading,
    auth0.isAuthenticated,
    auth0.getAccessTokenSilently,
    bootstrap,
  ]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        login,
        logout,
      }}
    >
      {!isInitialized && whenLoading}
      {isInitialized && !currentUser && whenUnAuthenticated}
      {isInitialized && !!currentUser && whenAuthenticated}
    </AuthContext.Provider>
  );
};
